/**
 * useStreamProcessor — Reusable streaming chunk handler.
 *
 * The streaming chunk processing logic was duplicated 4 times in the original
 * the original monolithic component (new chat stream, continuation stream, OAuth
 * retry stream x2). This hook extracts the common pattern into a factory.
 */

import { useCallback } from "react";

const MARKER = "\n[END OF MESSAGE]\n";

const stripPartialMarkerTail = (s, marker) => {
  const maxPrefix = Math.min(marker.length - 1, s.length);
  for (let len = maxPrefix; len > 0; len--) {
    if (s.endsWith(marker.slice(0, len))) return s.slice(0, s.length - len);
  }
  return s;
};

const replaceLastContent = (prev, content) => {
  const next = { content, is_user: false, loading: false };
  return prev.length === 0 ? [next] : [...prev.slice(0, -1), next];
};

/**
 * Creates stream handler callbacks for fetch_stream.
 *
 * @param {Object} deps - Shared chat state setters
 * @param {Function} deps.setMessages
 * @param {Function} deps.setQuestion
 * @param {Function} deps.setChatId
 * @param {Function} deps.handleExecutionStatus
 * @param {Function} deps.setIsLoading
 * @param {Function} deps.setWaitingOAuthServerId
 * @param {string|null} deps.iniMessage
 * @returns {{ onChunk: Function, onError: Function }}
 */
export const createStreamHandlers = ({
  setMessages,
  setQuestion,
  setChatId,
  handleExecutionStatus,
  setIsLoading,
  setWaitingOAuthServerId,
  iniMessage,
  onRequestError,
}) => {
  let rawBuffer = "";
  let markerIdx = -1;
  let jsonProcessed = false;

  const processJsonResponse = (jsonData) => {
    setQuestion(jsonData);
    setChatId(jsonData.id);

    if (jsonData.execution_status === "WAITING_FOR_AUTH") {
      if (setWaitingOAuthServerId) {
        setWaitingOAuthServerId(jsonData.server_id);
      }
      handleExecutionStatus(jsonData.execution_status);
      setMessages(
        iniMessage
          ? jsonData.messages.slice(1)
          : jsonData.messages,
      );
      setIsLoading(false);
      return;
    }

    handleExecutionStatus(jsonData.execution_status);

    if (iniMessage) {
      setMessages(jsonData.messages.slice(1));
    } else {
      setMessages(jsonData.messages);
    }
    setIsLoading(false);
  };

  const onChunk = (chunk) => {
    rawBuffer += chunk;

    if (markerIdx === -1) {
      markerIdx = rawBuffer.indexOf(MARKER);
    }

    if (markerIdx === -1) {
      const safeText = stripPartialMarkerTail(rawBuffer, MARKER);
      setMessages((prev) => replaceLastContent(prev, safeText));
      return;
    }

    const preMarkerText = rawBuffer.slice(0, markerIdx);
    setMessages((prev) => replaceLastContent(prev, preMarkerText));

    if (jsonProcessed) return;

    const jsonBuffer = rawBuffer.slice(markerIdx + MARKER.length).trim();
    if (!jsonBuffer) return;

    try {
      const jsonData = JSON.parse(jsonBuffer);
      jsonProcessed = true;
      processJsonResponse(jsonData);
    } catch {
      // JSON incomplete across chunks — wait for more data.
    }
  };

  const onError = (error, setStreamingError, setStatusModal) => {
    if (error?.type === "chatRunning") {
      if (setStreamingError) setStreamingError(error);
      if (setStatusModal) setStatusModal(true);
      return;
    }
    // Show an error modal
    if (setStreamingError) setStreamingError(error);
    if (onRequestError) onRequestError(error);
  };

  return { onChunk, onError };
};

/**
 * Hook that returns a memoized factory for creating stream handlers.
 * Use this when you need stable references across renders.
 */
export const useStreamProcessor = () => {
  const createHandlers = useCallback((deps) => {
    return createStreamHandlers(deps);
  }, []);

  return { createStreamHandlers: createHandlers };
};
